import { FC, PropsWithoutRef, Ref, forwardRef } from 'react';
import styled, { css } from 'styled-components';
import { mix, transparentize } from 'polished';

import { defaultThemeProp } from '../../theme';
import { tryCatch } from '../../utils/utils';
import { ForwardProps, PropsWithDefaults } from '../../types';
import { calculateFontSize } from '../../styles';

export interface CountProps {
  /** Determines the color to render the Badge as. This color is derived from the Theme.
   * @default "default"
   */
  variant?: 'default' | 'interactive' | 'inverted' | 'urgent';
  /** The count to be displayed within the Badge. */
  children: number | null;
}

type CountPropsWithDefaults = PropsWithDefaults<CountProps, 'variant'>;

export const StyledCount = styled.span<
  Pick<CountPropsWithDefaults, 'variant'> & { children: string }
>(props => {
  const { foreground, background } =
    props.variant === 'default'
      ? props.theme.components.badges.count.base
      : props.theme.components.badges.count[props.variant];

  const invertedBackground = tryCatch(() => transparentize(0.5, mix(0.2, background, '#ffffff')));

  const displayBackground = props.variant === 'inverted' ? invertedBackground : background;

  const borderRadius = props.theme.base['border-radius'];
  const { spacing } = props.theme.base;

  const fontSize = calculateFontSize(props.theme.base['font-size'], props.theme.base['font-scale']);

  return css`
    background: ${displayBackground};
    border-radius: calc(1.125 * ${borderRadius});
    color: ${foreground};
    display: inline-block;
    font-size: ${fontSize.xxs};
    font-weight: bold;
    line-height: normal;
    text-align: center;
    ${props.children.length > 1 &&
    css`
      padding: 0 ${spacing};
    `}
    ${props.children.length === 1 &&
    css`
      width: 1.125rem;
    `}
  `;
});

StyledCount.defaultProps = defaultThemeProp;

const Count: FC<ForwardProps & CountProps> = forwardRef(
  (props: PropsWithoutRef<CountProps>, ref: Ref<HTMLSpanElement>) => {
    const { variant = 'default', children, ...restProps } = props;
    return Number.isInteger(children) ? (
      <StyledCount variant={variant} {...restProps} ref={ref}>
        {(children as number) > 99 ? '99+' : `${children}`}
      </StyledCount>
    ) : null;
  }
);

export default Count;
